import type { components } from '@connected/api-client/api.generated';

import { api } from '@connected/api-client';

type DefaultFileOptions = {
    fileId: string | null;
};

type DefaultImageOptions = DefaultFileOptions & {
    quality: components['schemas']['ImageQuality'] | null;
};

// Naming convention: Prefix query option groups with "get" and suffix individual query functions with "Options"
const getDistributionBoardQueryOptions = {
    distributionBoardListOptions({
        electricalProjectVersionId,
    }: {
        electricalProjectVersionId: string;
    }) {
        return api.queryOptions(
            'get',
            '/api/v2/electrical/projects/distributionboards/list',
            {
                params: {
                    query: {
                        ElectricalProjectVersionId: electricalProjectVersionId,
                        Select: {
                            FloorNumber: true,
                            SpaceNumber: true,
                            Identifier: true,
                            IsPVInstallation: true,
                            Order: true,
                            Kind: true,
                            Files: true,
                            IsDeleted: true,
                            LastUpdateDate: true,
                        },
                        Sort: ['Order'],
                    },
                },
            },
        );
    },
    distributionBoardOptions({ // TODO: Bulk
        electricalProjectDistributionBoardId,
    }: {
        electricalProjectDistributionBoardId: string;
    }) {
        return api.queryOptions(
            'get',
            '/api/v2/electrical/projects/distributionboards/edit',
            {
                params: {
                    query: {
                        ElectricalProjectDistributionBoardId: electricalProjectDistributionBoardId,
                    },

                },
            },
        );
    },
    fileListQueryOptions({ // TODO: Bulk
        electricalProjectVersionId,
        electricalProjectDistributionBoardId,
    }: {
        electricalProjectVersionId: string;
        electricalProjectDistributionBoardId: string;
    }) {
        return api.queryOptions(
            'get',
            '/api/v2/electrical/projects/distributionboards/files/list',
            {
                params: {
                    query: {
                        ElectricalProjectVersionId: electricalProjectVersionId,
                        ElectricalProjectDistributionBoardId:
                            electricalProjectDistributionBoardId,
                        PageNumber: 1,
                        PageSize: 100,
                        Select: {
                            Order: true,
                            Kind: true,
                            Remark: true,
                            HasFile: true,
                            File: {
                                FileId: true,
                                FileName: true,
                                FileSize: true,
                                FileContentType: true,
                                ImageHeight: true,
                                ImageWidth: true,
                                IsImage: true,
                            },
                        },
                        Sort: ['Order'],
                    },
                },
            },
        );
    },
    checkListItemsOptions({ // TODO: Bulk
        electricalProjectVersionId,
        electricalProjectDistributionBoardId,
    }: {
        electricalProjectVersionId: string;
        electricalProjectDistributionBoardId: string;
    }) {
        return api.queryOptions(
            'get',
            '/api/v2/electrical/projects/checklists/items/list',
            {
                params: {
                    query: {
                        ElectricalProjectVersionId: electricalProjectVersionId,
                        ObjectKind: 'DistributionBoard',
                        ObjectId: electricalProjectDistributionBoardId,
                        PageNumber: 1,
                        PageSize: 100,
                        Select: {
                            ElectricalCheckListItemId: true,
                            Description: true,
                            ConclusionKind: true,
                            Order: true,
                        },
                        Sort: ['Order'],
                    },
                },
            },
        );
    },
    checklistTextGetOptions({ // TODO: Bulk (check if used)
        electricalProjectVersionId,
        electricalCheckListItemId,
    }: {
        electricalProjectVersionId: string;
        electricalCheckListItemId: string;
    }) {
        return api.queryOptions(
            'get',
            '/api/v2/electrical/projects/checklists/texts/list',
            {
                params: {
                    query: {
                        ElectricalProjectVersionId: electricalProjectVersionId,
                        ElectricalCheckListItemId: electricalCheckListItemId,
                    },
                },
            },
        );
    },
    options: {
        distributionBoardValuesOptions() {
            return api.queryOptions(
                'get',
                '/api/v2/electrical/projects/distributionboards/values/list',
            );
        },
        ingressProtectionRatingsOptions() {
            return api.queryOptions(
                'get',
                '/api/v2/electrical/projects/ingressprotectionratings/list',
            );
        },
        residualCurrentDeviceOptionsListOptions() {
            return api.queryOptions(
                'get',
                '/api/v2/electrical/projects/distributionboards/devices/values/list',
            );
        },
    },
};

const getFinalCircuitQueryOptions = {
    finalCircuitListOptions({
        electricalProjectVersionId,
    }: {
        electricalProjectVersionId: string;
    }) {
        return api.queryOptions('get', '/api/v2/electrical/projects/finalcircuits/list', {
            params: {
                query: {
                    ElectricalProjectVersionId: electricalProjectVersionId,
                    PageNumber: 1,
                    PageSize: 100,
                    Select: {
                        FloorNumber: true,
                        SpaceNumber: true,
                        Identifier: true,
                        Order: true,
                        Kind: true,
                    },
                    Sort: ['Order'],
                },
            },
        });
    },
    finalCircuitOptions({
        electricalProjectFinalCircuitId,
    }: {
        electricalProjectFinalCircuitId: string;
    }) {
        return api.queryOptions('get', '/api/v2/electrical/projects/finalcircuits/edit', {
            params: {
                query: {
                    ElectricalProjectFinalCircuitId:
                        electricalProjectFinalCircuitId,
                },
            },
        });
    },
    imagesQueryOptions({
        electricalProjectVersionId,
        electricalProjectFinalCircuitId,
    }: {
        electricalProjectVersionId: string;
        electricalProjectFinalCircuitId: string;
    }) {
        return api.queryOptions(
            'get',
            '/api/v2/electrical/projects/finalcircuits/files/list',
            {
                params: {
                    query: {
                        ElectricalProjectVersionId: electricalProjectVersionId,
                        ElectricalProjectFinalCircuitId:
                            electricalProjectFinalCircuitId,
                        PageNumber: 1,
                        PageSize: 100,
                        Select: {
                            ElectricalProjectFileId: true,
                            ElectricalProjectFinalCircuitId: true,
                            Kind: true,
                            HasFile: true,
                            File: {
                                FileId: true,
                                FileName: true,
                                FileSize: true,
                                FileContentType: true,
                                ImageHeight: true,
                                ImageWidth: true,
                                IsImage: true,
                            },
                        },
                        Sort: ['Order'],
                    },
                },
            },
        );
    },
    checkListItemsOptions({
        electricalProjectVersionId,
        electricalProjectFinalCircuitId,
    }: {
        electricalProjectVersionId: string;
        electricalProjectFinalCircuitId: string;
    }) {
        return api.queryOptions(
            'get',
            '/api/v2/electrical/projects/checklists/items/list',
            {
                params: {
                    query: {
                        ElectricalProjectVersionId: electricalProjectVersionId,
                        ObjectKind: 'FinalCircuit',
                        ObjectId: electricalProjectFinalCircuitId,
                        PageNumber: 1,
                        PageSize: 100,
                        Select: {
                            ElectricalCheckListItemId: true,
                            Description: true,
                            ConclusionKind: true,
                            Order: true,
                        },
                        Sort: ['Order'],
                    },
                },
            },
        );
    },
};

const getPVInverterQueryOptions = {
    PVInverterListOptions({
        electricalProjectVersionId,
    }: {
        electricalProjectVersionId: string;
    }) {
        return api.queryOptions('get', '/api/v2/electrical/projects/pvinverters/list', {
            params: {
                query: {
                    ElectricalProjectVersionId: electricalProjectVersionId,
                    Sort: ['Order'],
                },
            },
        });
    },
};

const getObservationQueryOptions = {
    classificationGetOptions({
        electricalProjectVersionId,
    }: {
        electricalProjectVersionId: string;
    }) {
        return api.queryOptions(
            'get',
            '/api/v2/electrical/projects/observations/classifications/list',
            {
                params: {
                    query: {
                        ElectricalProjectVersionId: electricalProjectVersionId,
                    },
                },
            },
        );
    },
    standardTextsGetOptions({
        electricalProjectVersionId,
        objectKind,
    }: {
        electricalProjectVersionId: string;
        objectKind: components['schemas']['ElectricalObservationStandardObjectKind'];
    }) {
        return api.queryOptions(
            'get',
            '/api/v2/electrical/projects/observations/standardtexts/items/list',
            {
                params: {
                    query: {
                        ElectricalProjectVersionId: electricalProjectVersionId,
                        ObjectKind: objectKind,
                    },
                },
            },
        );
    },
    observationFileListQueryOptions({
        electricalProjectVersionId,
        electricalProjectObservationId,
    }: {
        electricalProjectVersionId: string;
        electricalProjectObservationId: string;
    }) {
        return api.queryOptions(
            'get',
            '/api/v2/electrical/projects/observations/files/list',
            {
                params: {
                    query: {
                        ElectricalProjectVersionId: electricalProjectVersionId,
                        ElectricalProjectObservationId:
                            electricalProjectObservationId,
                        PageNumber: 1,
                        PageSize: 100,
                        Sort: ['Order'],
                        Select: {
                            ElectricalProjectObservationId: true,
                            ElectricalProjectFileId: true,
                            Order: true,
                            HasFile: true,
                            File: {
                                FileId: true,
                                FileName: true,
                                FileSize: true,
                                FileContentType: true,
                                ImageHeight: true,
                                ImageWidth: true,
                                IsImage: true,
                            },
                        },
                    },
                },
            },
        );
    },
};

export const elektraAppQueryOptions = {
    files: {
        projectImage: {
            download: (options: DefaultImageOptions & { electricalProjectFileId: string }) => {
                return api.queryOptions(
                    'get',
                    '/api/v2/electrical/projects/files/download',
                    {
                        params: {
                            query: {
                                ElectricalProjectFileId: options.electricalProjectFileId,
                                FileId: options.fileId,
                                ImageQuality: options.quality,
                            },
                        },
                        parseAs: 'blob',
                    },
                );
            },
            upload: () => api.queryOptions('get', '/api/v2/electrical/projects/files/upload/objectimage'),
            list: ({ electricalProjectVersionId }: { electricalProjectVersionId: string }) => api.queryOptions('get', '/api/v2/electrical/projects/files/list', {
                params: {
                    query: {
                        ElectricalProjectVersionId: electricalProjectVersionId,
                        Select: {
                            Description: true,
                            ElectricalProjectVersionId: true,
                            File: {
                                FileContentType: true,
                                FileId: true,
                                FileName: true,
                                FileSize: true,
                                ImageHeight: true,
                                ImageWidth: true,
                                IsImage: true,
                                LastUpdateDate: true,
                            },
                            HasFile: true,
                            Kind: true,
                            LastUpdateDate: true,
                            Order: true,
                        },
                        Sort: ['Order'],
                        IsDeleted: 'NotDeleted',
                    },
                },
            }),
        },
        distributionBoardImage: {
            download: (options: DefaultImageOptions & { electricalProjectDistributionBoardFileId: string }) => {
                return api.queryOptions(
                    'get',
                    '/api/v2/electrical/projects/distributionboards/files/download',
                    {
                        params: {
                            query: {
                                ElectricalProjectDistributionBoardFileId: options.electricalProjectDistributionBoardFileId,
                                FileId: options.fileId,
                                ImageQuality: options.quality,
                            },
                        },
                        parseAs: 'blob',
                    },
                );
            },
            upload: () => api.queryOptions('get', '/api/v2/electrical/projects/files/upload/distributionboard'),
            list: ({ electricalProjectVersionId, electricalProjectDistributionBoardId }: { electricalProjectVersionId: string; electricalProjectDistributionBoardId: string }) => api.queryOptions('get', '/api/v2/electrical/projects/distributionboards/files/list', {
                params: {
                    query: {
                        ElectricalProjectVersionId: electricalProjectVersionId,
                        ElectricalProjectDistributionBoardId: electricalProjectDistributionBoardId,
                        Select: {
                            Description: true,
                            ElectricalProjectVersionId: true,
                            File: {
                                FileId: true,
                                FileName: true,
                                FileSize: true,
                                FileContentType: true,
                                ImageHeight: true,
                                ImageWidth: true,
                                IsImage: true,
                                LastUpdateDate: true,
                            },
                            HasFile: true,
                            Kind: true,
                            LastUpdateDate: true,
                            Order: true,
                        },
                        Sort: ['Order'],
                        IsDeleted: 'NotDeleted',
                    },
                },
            }),
        },
        finalCircuitImage: {
            download: (options: DefaultImageOptions & { electricalProjectFinalCircuitFileId: string }) => {
                return api.queryOptions(
                    'get',
                    '/api/v2/electrical/projects/finalcircuits/files/download',
                    {
                        params: {
                            query: {
                                ElectricalProjectFinalCircuitFileId: options.electricalProjectFinalCircuitFileId,
                                FileId: options.fileId,
                                ImageQuality: options.quality,
                            },
                        },
                        parseAs: 'blob',
                    },
                );
            },
            upload: () => api.queryOptions('get', '/api/v2/electrical/projects/files/upload/finalcircuit'),
            list: ({ electricalProjectVersionId, electricalProjectFinalCircuitId }: { electricalProjectVersionId: string; electricalProjectFinalCircuitId: string }) => api.queryOptions('get', '/api/v2/electrical/projects/finalcircuits/files/list', {
                params: {
                    query: {
                        ElectricalProjectVersionId: electricalProjectVersionId,
                        ElectricalProjectFinalCircuitId: electricalProjectFinalCircuitId,
                        Select: {
                            ElectricalProjectFileId: true,
                            ElectricalProjectFinalCircuitId: true,
                            Order: true,
                            HasFile: true,
                            File: {
                                FileId: true,
                                FileName: true,
                                FileSize: true,
                                FileContentType: true,
                                ImageHeight: true,
                                ImageWidth: true,
                                IsImage: true,
                                LastUpdateDate: true,
                            },
                            IsDeleted: true,
                            Kind: true,
                            LastUpdateDate: true,
                        },
                        Sort: ['Order'],
                        IsDeleted: 'NotDeleted',
                    },
                },
            }),
        },
        observationImage: {
            download: (options: DefaultImageOptions & { electricalProjectObservationFileId: string }) => {
                return api.queryOptions(
                    'get',
                    '/api/v2/electrical/projects/observations/files/download',
                    {
                        params: {
                            query: {
                                ElectricalProjectObservationFileId: options.electricalProjectObservationFileId,
                                FileId: options.fileId,
                                ImageQuality: options.quality,
                            },
                        },
                        parseAs: 'blob',
                    },
                );
            },
            upload: () => api.queryOptions('get', '/api/v2/electrical/projects/files/upload/observation'),
            list: ({ electricalProjectVersionId, electricalProjectObservationId }: { electricalProjectVersionId: string; electricalProjectObservationId: string }) => api.queryOptions('get', '/api/v2/electrical/projects/observations/files/list', {
                params: {
                    query: {
                        ElectricalProjectVersionId: electricalProjectVersionId,
                        ElectricalProjectObservationId: electricalProjectObservationId,
                        Select: {
                            ElectricalProjectObservationId: true,
                            ElectricalProjectFileId: true,
                            Order: true,
                            HasFile: true,
                            File: {
                                FileId: true,
                                FileName: true,
                                FileSize: true,
                                FileContentType: true,
                                ImageHeight: true,
                                ImageWidth: true,
                                IsImage: true,
                                LastUpdateDate: true,
                            },
                            IsDeleted: true,
                            Kind: true,
                            LastUpdateDate: true,
                        },
                        Sort: ['Order'],
                        IsDeleted: 'NotDeleted',
                    },
                },
            }),
        },
        technicalDrawingImage: {
            download: (options: DefaultImageOptions & { electricalProjectTechnicalDrawingId: string }) => {
                return api.queryOptions(
                    'get',
                    '/api/v2/electrical/projects/technicaldrawings/files/download',
                    {
                        params: {
                            query: {
                                ElectricalProjectTechnicalDrawingId: options.electricalProjectTechnicalDrawingId,
                                FileId: options?.fileId ?? null,
                                ImageQuality: options.quality,
                            },
                        },
                        parseAs: 'blob',
                    },
                );
            },
            upload: () => api.queryOptions('get', '/api/v2/electrical/projects/files/upload/technicaldrawing'),
            list: ({ electricalProjectVersionId }: { electricalProjectVersionId: string }) => api.queryOptions('get', '/api/v2/electrical/projects/technicaldrawings/list', {
                params: {
                    query: {
                        ElectricalProjectVersionId: electricalProjectVersionId,
                        Select: {
                            Description: true,
                            ElectricalProjectFileId: true,
                            File: {
                                FileId: true,
                                FileName: true,
                                FileSize: true,
                                FileContentType: true,
                                ImageHeight: true,
                                ImageWidth: true,
                                IsImage: true,
                                LastUpdateDate: true,
                            },
                            HasFile: true,
                            IsDeleted: true,
                            IsDrawingPresent: true,
                            LastUpdateDate: true,
                            Order: true,
                            Location: true,
                            Name: true,
                        },
                        Sort: ['NameDesc'],
                    },
                },
            }),
        },
        floorPlanImage: {
            upload: () => api.queryOptions('get', '/api/v2/electrical/projects/files/upload/floorplan'),
        },
        pvInverterImage: {
            upload: () => api.queryOptions('get', '/api/v2/electrical/projects/files/upload/pvinverter'),
        },
        pvInstallation: {
            upload: () => api.queryOptions('get', '/api/v2/electrical/projects/files/upload/pvinstallation'),
        },
        organizationLogoImage: {
            download: (options: DefaultImageOptions & { organizationFileId: string }) => {
                return api.queryOptions(
                    'get',
                    '/api/v2/organizations/files/download',
                    {
                        params: {
                            query: {
                                OrganizationFileId: options.organizationFileId,
                                ImageQuality: options.quality,
                                FileId: options.fileId,
                            },
                        },
                        parseAs: 'blob',
                    },
                );
            },
        },
    },
    project: {
        edit: ({ electricalProjectVersionId }: { electricalProjectVersionId: string }) => api.queryOptions('get', '/api/v2/electrical/projects/edit', {
            params: {
                query: {
                    ElectricalProjectVersionId: electricalProjectVersionId,
                },
            },
        }),
        status: ({ electricalProjectVersionId }: { electricalProjectVersionId: string }) => api.queryOptions('get', '/api/v2/electrical/projects/status', {
            params: {
                query: {
                    ElectricalProjectVersionId: electricalProjectVersionId,
                },
            },
        }),
        request: {
            inspectionReportsListOptions({
                electricalProjectVersionId,
            }: {
                electricalProjectVersionId: string;
            }) {
                return api.queryOptions(
                    'get',
                    '/api/v2/electrical/projects/documents/templates/list',
                    {
                        params: {
                            query: {
                                ElectricalProjectVersionId: electricalProjectVersionId,
                                GroupKind: 'ElectricalProjectReport',
                            },
                        },
                    },
                );
            },
            objectOptions({
                electricalProjectVersionId,
            }: {
                electricalProjectVersionId: string;
            }) {
                return api.queryOptions('get', '/api/v2/electrical/projects/objects/edit', {
                    params: {
                        query: {
                            ElectricalProjectVersionId: electricalProjectVersionId,
                        },
                    },
                });
            },
            organizationOptions({
                electricalProjectVersionId,
            }: {
                electricalProjectVersionId: string;
            }) {
                return api.queryOptions('get', '/api/v2/electrical/projects/organizations/list', {
                    params: {
                        query: {
                            ElectricalProjectVersionId: electricalProjectVersionId,
                            PageNumber: 1,
                            PageSize: 100,
                            Sort: ['Name'],
                            Select: {
                                Name: true,
                                Address: {
                                    AdministrativeArea: true,
                                    CountryId: true,
                                    CountryName: true,
                                    Locality: true,
                                    PostalCode: true,
                                    Street: true,
                                    StreetNumber: true,
                                },
                                Description: true,
                                Email: true,
                                IsObjectAdministrator: true,
                                IsObjectOwner: true,
                                IsObjectTenant: true,
                                IsProjectCustomer: true,
                                IsPVInstallationConstructionCompany: true,
                                TelephoneNumber: true,
                                VatNumber: true,
                                Website: true,
                                BusinessRegistration: {
                                    BranchNumber: true,
                                    Number: true,
                                },
                            },
                            IncludeContacts: true,
                            SelectContact: {
                                Department: true,
                                Email: true,
                                FirstName: true,
                                JobTitleDescription: true,
                                LastName: true,
                                MiddleName: true,
                                MobilePhoneNumber: true,
                                ReceiveInspectionPlan: true,
                                ReceiveInspectionReport: true,
                                ReceiveInvoice: true,
                                ReceivePowerShutdownPlan: true,
                                ReceivePriceQuote: true,
                                IsResponsibleForPowerSystem: true,
                            },
                        },
                    },
                });
            },
            technicalDrawingsQueryOptions({
                electricalProjectVersionId,
            }: {
                electricalProjectVersionId: string;
            }) {
                return api.queryOptions(
                    'get',
                    '/api/v2/electrical/projects/technicaldrawings/list',
                    {
                        params: {
                            query: {
                                ElectricalProjectVersionId: electricalProjectVersionId,
                                PageNumber: 1,
                                PageSize: 100,
                                Select: {
                                    Description: true,
                                    ElectricalProjectFileId: true,
                                    File: {
                                        FileContentType: true,
                                        FileId: true,
                                        FileName: true,
                                        FileSize: true,
                                        ImageHeight: true,
                                        ImageWidth: true,
                                        IsImage: true,
                                        LastUpdateDate: true,
                                    },
                                    HasFile: true,
                                    IsDeleted: true,
                                    IsDrawingPresent: true,
                                    LastUpdateDate: true,
                                    Order: true,
                                    Location: true,
                                    Name: true,
                                },
                                Sort: ['NameDesc'],
                            },
                        },
                    },
                );
            },

        },
        quotation: {
            quotationOptions({
                electricalProjectVersionId,
            }: {
                electricalProjectVersionId: string;
            }) {
                return api.queryOptions('get', '/api/v2/electrical/projects/pricequotes/edit', {
                    params: {
                        query: {
                            ElectricalProjectVersionId: electricalProjectVersionId,
                        },
                    },
                });
            },
        },
        inspectionPlan: {
            inspectionPlanEditOptions({
                electricalProjectVersionId,
            }: {
                electricalProjectVersionId: string;
            }) {
                return api.queryOptions(
                    'get',
                    '/api/v2/electrical/projects/inspectionplans/edit',
                    {
                        params: {
                            query: {
                                ElectricalProjectVersionId: electricalProjectVersionId,
                            },
                        },
                    },
                );
            },
            inspectionNormsListOptions({
                electricalProjectVersionId,
            }: {
                electricalProjectVersionId: string;
            }) {
                return api.queryOptions(
                    'get',
                    '/api/v2/electrical/projects/inspectionnorms/list',
                    {
                        params: {
                            query: {
                                ElectricalProjectVersionId: electricalProjectVersionId,
                                Sort: ['Order', 'Description'],
                                PageNumber: 1,
                                PageSize: 100,
                                Select: {
                                    ElectricalInspectionNormItemId: true,
                                    InspectionKinds: true,
                                    Name: true,
                                    Description: true,
                                    CustomDescription: true,
                                    Order: true,
                                    Status: true,
                                    Remarks: true,
                                    Version: true,
                                    LastUpdateDate: true,
                                },
                            },
                        },
                    },
                );
            },
        },
        schedule: {
            scheduleOptions({
                electricalProjectVersionId,
            }: {
                electricalProjectVersionId: string;
            }) {
                return api.queryOptions(
                    'get',
                    '/api/v2/electrical/projects/inspections/schedules/edit',
                    {
                        params: {
                            query: {
                                ElectricalProjectVersionId: electricalProjectVersionId,
                            },
                        },
                    },
                );
            },
            projectUsersListOptions({
                electricalProjectVersionId,
                userKind,
            }: {
                electricalProjectVersionId: string;
                userKind: components['schemas']['ElectricalProjectUserKind'];
            }) {
                return api.queryOptions('get', '/api/v2/electrical/projects/users/list', {
                    params: {
                        query: {
                            ElectricalProjectVersionId: electricalProjectVersionId,
                            Kind: userKind,
                            Sort: ['DisplayName'],
                            IncludeOnlyProjectUsers: false,
                            LastUpdateDateAfter: null,
                            PageNumber: 1,
                            PageSize: 100,
                            Select: {
                                DisplayName: true,
                                ElectricalProjectUserId: true,
                                IsDeleted: true,
                                Kind: true,
                                LastUpdateDate: true,
                                Selected: true,
                            },
                        },
                    },
                });
            },
        },
        inspection: {
            information: {
                inspectionsEditOptions({
                    electricalProjectVersionId,
                }: {
                    electricalProjectVersionId: string;
                }) {
                    return api.queryOptions('get', '/api/v2/electrical/projects/inspections/edit', {
                        params: {
                            query: {
                                ElectricalProjectVersionId: electricalProjectVersionId,
                            },
                        },
                    });
                },
                selectableMeasurementDevicesListOptions({
                    electricalProjectVersionId,
                }: {
                    electricalProjectVersionId: string;
                }) {
                    return api.queryOptions(
                        'get',
                        '/api/v2/electrical/projects/measurementdevices/create',
                        {
                            params: {
                                query: {
                                    ElectricalProjectVersionId: electricalProjectVersionId,
                                    PageNumber: 1,
                                    PageSize: 100,
                                },
                            },
                        },
                    );
                },
                selectedMeasurementDevicesListOptions({
                    electricalProjectVersionId,
                }: {
                    electricalProjectVersionId: string;
                }) {
                    return api.queryOptions(
                        'get',
                        '/api/v2/electrical/projects/measurementdevices/list',
                        {
                            params: {
                                query: {
                                    ElectricalProjectVersionId: electricalProjectVersionId,
                                    PageNumber: 1,
                                    PageSize: 100,
                                },
                            },
                        },
                    );
                },
            },
            distributionBoard: getDistributionBoardQueryOptions,
            finalCircuit: getFinalCircuitQueryOptions,
            pvinverter: getPVInverterQueryOptions,
            observations: getObservationQueryOptions,
            controlPoints: {
                ebMeasurementEditList({
                    electricalProjectVersionId,
                }: {
                    electricalProjectVersionId: string;
                }) {
                    return api.queryOptions(
                        'get',
                        '/api/v2/electrical/projects/ebmeasurements/list',
                        {
                            params: {
                                query: {
                                    ElectricalProjectVersionId: electricalProjectVersionId,
                                },
                            },
                        },
                    );
                },
            },
            conclusion: {
                conclusionEditOptions({
                    electricalProjectVersionId,
                }: {
                    electricalProjectVersionId: string;
                }) {
                    return api.queryOptions(
                        'get',
                        '/api/v2/electrical/projects/inspections/conclusions/edit',
                        {
                            params: {
                                query: {
                                    ElectricalProjectVersionId: electricalProjectVersionId,
                                },
                            },
                        },
                    );
                },
                observationLogsListOptions({
                    electricalProjectVersionId,
                }: {
                    electricalProjectVersionId: string;
                }) {
                    return api.queryOptions(
                        'get',
                        '/api/v2/electrical/projects/observations/logs/list',
                        {
                            params: {
                                query: {
                                    ElectricalProjectVersionId: electricalProjectVersionId,
                                },
                            },
                        },
                    );
                },
            },
        },
        files: {
            listOptions({ electricalProjectVersionId }: { electricalProjectVersionId: string }) {
                return api.queryOptions('get', '/api/v2/electrical/projects/files/list', {
                    params: {
                        query: {
                            ElectricalProjectVersionId: electricalProjectVersionId,
                            Select: {
                                ElectricalProjectVersionId: true,
                                Kind: true,
                                Order: true,
                                Description: true,
                                HasFile: true,
                                File: {
                                    FileId: true,
                                    FileName: true,
                                    FileSize: true,
                                    FileContentType: true,
                                    IsImage: true,
                                    ImageWidth: true,
                                    ImageHeight: true,
                                    LastUpdateDate: true,
                                },
                                LastUpdateDate: true,
                            },
                            Sort: ['Order'],
                        },
                    },
                });
            },
        },
        selectListsItems: {
            selectListsItemsGetOptions({
                electricalProjectVersionId,
                listKind,
            }: {
                electricalProjectVersionId: string;
                listKind: components['schemas']['ElectricalProjectSelectListKind'];
            }) {
                return api.queryOptions(
                    'get',
                    '/api/v2/electrical/projects/selectlists/items/list',
                    {
                        params: {
                            query: {
                                ElectricalProjectVersionId: electricalProjectVersionId,
                                ListKind: listKind,
                            },
                        },
                    },
                );
            },
        },
        recapViewOptions({
            electricalProjectVersionId,
        }: {
            electricalProjectVersionId: string;
        }) {
            return api.queryOptions('get', '/api/v2/electrical/projects/summaries/view', {
                params: {
                    query: {
                        ElectricalProjectVersionId: electricalProjectVersionId,
                    },
                },
            });
        },
        recapOptions({
            electricalProjectVersionId,
        }: {
            electricalProjectVersionId: string;
        }) {
            return api.queryOptions('get', '/api/v2/electrical/projects/requests/summary', {
                params: {
                    query: {
                        ElectricalProjectVersionId: electricalProjectVersionId,
                    },
                },
            });
        },
        observationsListOptions({
            electricalProjectVersionId,
        }: {
            electricalProjectVersionId: string;
        }) {
            return api.queryOptions('get', '/api/v2/electrical/projects/observations/list', {
                params: {
                    query: {
                        ElectricalProjectVersionId: electricalProjectVersionId,
                        Select: {
                            CodeHexColorCode: true,
                            CodeId: true,
                            CodeKind: true,
                            GroupDescription: true,
                            GroupId: true,
                            LocationRemark: true,
                            Order: true,
                            Recommendation: true,
                            Remark: true,
                            RecommendationTextId: true,
                            RemarkTextId: true,
                            ElectricalProjectCheckListItemId: true,
                            ElectricalProjectDistributionBoardDeviceId: true,
                            ElectricalProjectDistributionBoardFileId: true,
                            ElectricalProjectDistributionBoardId: true,
                            ElectricalProjectFinalCircuitId: true,
                            ElectricalProjectRisoMeasurementId: true,
                            ElectricalProjectFileIds: true,
                            ElectricalProjectPVInverterId: true,
                            ObjectKind: true,
                            IsDeleted: true,
                            Reports: true,
                            LastUpdateDate: true,
                        },
                        Sort: ['Order'],
                    },
                },
            });
        },
        checklistTextGetOptions({
            electricalProjectVersionId,
        }: {
            electricalProjectVersionId: string;
        }) {
            return api.queryOptions(
                'get',
                '/api/v2/electrical/projects/checklists/texts/list',
                {
                    params: {
                        query: {
                            ElectricalProjectVersionId: electricalProjectVersionId,
                        },
                    },
                },
            );
        },
        distributionBoardDeviceListOptions({
            electricalProjectVersionId,
        }: {
            electricalProjectVersionId: string;
        }) {
            return api.queryOptions(
                'get',
                '/api/v2/electrical/projects/distributionboards/devices/list',
                {
                    params: {
                        query: {
                            ElectricalProjectVersionId: electricalProjectVersionId,
                            Select: {
                                ElectricalProjectVersionId: true,
                                Identifier: true,
                                ResidualCurrentDeviceType: true,
                                RatedCurrent: true,
                                TrippingCharacteristic: true,
                                BreakerRatedCurrent: true,
                                Order: true,
                                TripDelay: true,
                                RatedResidualCurrent: true,
                                IsTestButtonFunctional: true,
                                DeviceKind: true,
                                ElectricalProjectDistributionBoardId: true,
                                PowerPhaseKind: true,
                                MeasureResistanceBetweenPowerPhases: true,
                                IsDeleted: true,
                                ResistanceL1L2: true,
                                ResistanceL1L3: true,
                                ResistanceL2L3: true,
                                ResistanceL2PE: true,
                                ResistanceL3PE: true,
                                ResistanceNeutralPE: true,
                                ResistanceMeasurement: true,
                                LastUpdateDate: true,
                                ResistanceL1Neutral: true,
                                ResistanceL2Neutral: true,
                                ResistanceL3Neutral: true,
                                ResistanceL1PE: true,
                            },
                            Sort: ['Order'],
                        },
                    },
                },
            );
        },
        /**
         * Query options for fetching devices for a specific distribution board.
         * Use this instead of distributionBoardDeviceListOptions when you only need
         * devices for a single board to avoid fetching all project devices.
         */
        distributionBoardDeviceListByBoardOptions({
            electricalProjectVersionId,
            electricalProjectDistributionBoardId,
        }: {
            electricalProjectVersionId: string;
            electricalProjectDistributionBoardId: string;
        }) {
            return api.queryOptions(
                'get',
                '/api/v2/electrical/projects/distributionboards/devices/list',
                {
                    params: {
                        query: {
                            ElectricalProjectVersionId: electricalProjectVersionId,
                            ElectricalProjectDistributionBoardId: electricalProjectDistributionBoardId,
                            Select: {
                                ElectricalProjectVersionId: true,
                                Identifier: true,
                                ResidualCurrentDeviceType: true,
                                RatedCurrent: true,
                                TrippingCharacteristic: true,
                                BreakerRatedCurrent: true,
                                Order: true,
                                TripDelay: true,
                                RatedResidualCurrent: true,
                                IsTestButtonFunctional: true,
                                DeviceKind: true,
                                ElectricalProjectDistributionBoardId: true,
                                PowerPhaseKind: true,
                                MeasureResistanceBetweenPowerPhases: true,
                                IsDeleted: true,
                                ResistanceL1L2: true,
                                ResistanceL1L3: true,
                                ResistanceL2L3: true,
                                ResistanceL2PE: true,
                                ResistanceL3PE: true,
                                ResistanceNeutralPE: true,
                                ResistanceMeasurement: true,
                                LastUpdateDate: true,
                                ResistanceL1Neutral: true,
                                ResistanceL2Neutral: true,
                                ResistanceL3Neutral: true,
                                ResistanceL1PE: true,
                            },
                            Sort: ['Order'],
                        },
                    },
                },
            );
        },
        risoMeasurementOptions({
            electricalProjectVersionId,
        }: {
            electricalProjectVersionId: string;
        }) {
            return api.queryOptions(
                'get',
                '/api/v2/electrical/projects/riso/measurements/list',
                {
                    params: {
                        query: {
                            ElectricalProjectVersionId: electricalProjectVersionId,
                        },
                    },
                },
            );
        },
        validateOptions({
            electricalProjectVersionId,
            kind,
        }: {
            electricalProjectVersionId: string;
            kind: components['schemas']['CreateElectricalProjectDocumentKind'];
        }) {
            return api.queryOptions(
                'get',
                '/api/v2/electrical/projects/validate',
                {
                    params: {
                        query: {
                            ElectricalProjectVersionId: electricalProjectVersionId,
                            Kind: kind,
                        },
                    },
                },
            );
        },
        publishProjectOptions({
            electricalProjectVersionId,
        }: {
            electricalProjectVersionId: string;
        }) {
            return api.queryOptions(
                'get',
                '/api/v2/electrical/projects/publish',
                {
                    params: {
                        query: {
                            ElectricalProjectVersionId: electricalProjectVersionId,
                        },
                    },
                },
            );
        },
        checkListItemsOptions({
            electricalProjectVersionId,
        }: {
            electricalProjectVersionId: string;
        }) {
            return api.queryOptions(
                'get',
                '/api/v2/electrical/projects/checklists/items/list',
                {
                    params: {
                        query: {
                            ElectricalProjectVersionId: electricalProjectVersionId,
                            Select: {
                                ElectricalCheckListItemId: true,
                                Description: true,
                                ConclusionKind: true,
                                Order: true,
                                ElectricalProjectDistributionBoardId: true,
                                ElectricalProjectFinalCircuitId: true,
                                ObjectKind: true,
                            },
                            Sort: ['Order'],
                        },
                    },
                },
            );
        },

    },
    admin: {
        organisationsListOptions() {
            return api.queryOptions('get', '/api/v2/organizations/list', {
                params: {
                    query: {
                        PageNumber: 1,
                        PageSize: 100,
                        Select: {
                            Name: true,
                            Description: true,
                            ElectricalOrganizationKind: true,
                            LogoOrganizationFileId: true,
                            ChamberOfCommerceNumber: true,
                            LegalPersonKind: true,
                        },
                        Sort: ['Name'],
                    },
                },
            });
        },
    },
    organisation: {
        projects: {
            projectListOptions({
                organizationId,
                enabled = true,
            }: { organizationId?: string; enabled?: boolean } = {}) {
                return api.queryOptions(
                    'get',
                    '/api/v2/electrical/projects/list',
                    {
                        params: {
                            query: {
                                OrganizationId: organizationId!,
                                Select: {
                                    Locality: true,
                                    CreateDate: true,
                                    CurrentElectricalProjectVersionId: true,
                                    PostalCode: true,
                                    Street: true,
                                    StreetNumber: true,
                                    Reports: true,
                                    Status: true,
                                    ObjectId: true,
                                    Users: true,
                                    LastActivityDate: true,
                                    LastActivityUser: true,
                                },
                                Sort: ['CreateDateDesc'],
                                PageNumber: 1,
                                PageSize: 100,
                            },
                        },
                    },
                    {
                        enabled,
                    },
                );
            },
            requestListOptions({
                organizationId,
                enabled = true,
            }: { organizationId?: string; enabled?: boolean } = {}) {
                return api.queryOptions(
                    'get',
                    '/api/v2/electrical/projects/list',
                    {
                        params: {
                            query: {
                                OrganizationId: organizationId!,
                                Select: {
                                    Locality: true,
                                    CreateDate: true,
                                    CurrentElectricalProjectVersionId: true,
                                    PostalCode: true,
                                    Street: true,
                                    StreetNumber: true,
                                    Reports: true,
                                    Status: true,
                                    ObjectId: true,
                                    LastActivityDate: true,
                                    LastActivityUser: true,
                                    Users: true,
                                },
                                Sort: ['CreateDateDesc'],
                                Status: 'Request',
                                PageNumber: 1,
                                PageSize: 100,
                            },
                        },
                    },
                    {
                        enabled,
                    },
                );
            },
            documentListOptions({
                electricalProjectVersionId,
            }: {
                electricalProjectVersionId: string;
            }) {
                return api.queryOptions('get', '/api/v2/electrical/projects/documents/list', {
                    params: {
                        query: {
                            ElectricalProjectVersionId: electricalProjectVersionId,
                            PageNumber: 1,
                            PageSize: 100,
                            Select: {
                                CreateUserId: true,
                                CreateUserDisplayName: true,
                                FileName: true,
                                CreateDate: true,
                                VersionKind: true,
                                DocumentStatus: true,
                                ContentKind: true,
                                DocumentTemplateId: true,
                                FileKind: true,
                                Status: true,
                                ElectricalProjectObservationId: true,
                                AccessKind: true,
                                Description: true,
                                SerialNumber: true,
                                ElectricalProjectCheckListItemObservationId: true,
                                InspectionKind: true,
                            },
                            Sort: ['CreateDateDesc'],
                        },
                    },
                });
            },
        },
        management: {
            organisationGetOptions({ organisationId }: { organisationId: string }) {
                return api.queryOptions('get', '/api/v2/organizations/edit', {
                    params: {
                        query: {
                            OrganizationId: organisationId,
                        },
                    },
                });
            },
            usersListOptions({ organisationId }: { organisationId: string }) {
                return api.queryOptions('get', '/api/v2/users/list', {
                    params: {
                        query: {
                            OrganizationId: organisationId,
                            PageNumber: 1,
                            PageSize: 100,
                            Select: {
                                Status: true,
                                Description: true,
                            },
                            Sort: ['Email'],
                        },
                    },
                });
            },
            measurementDevicesCreateOptions({
                organisationId,
            }: {
                organisationId: string;
            }) {
                return api.queryOptions('get', '/api/v2/electrical/measurementdevices/create', {
                    params: {
                        query: {
                            OrganizationId: organisationId,
                        },
                    },
                });
            },

            organisationRolesGetOptions({ organisationId }: { organisationId: string }) {
                return api.queryOptions('get', '/api/v2/users/organizations/create', {
                    params: {
                        query: {
                            OrganizationId: organisationId,
                        },
                    },
                });
            },

            normsListOptions({ organisationId }: { organisationId: string }) {
                return api.queryOptions('get', '/api/v2/electrical/inspectionnorms/lists/list', {
                    params: {
                        query: {
                            OrganizationId: organisationId,
                            PageNumber: 1,
                            PageSize: 100,
                            Select: {
                                Status: true,
                                Description: true,
                            },
                            Sort: ['Status'],
                        },
                    },
                });
            },

            observationStandardTextsOptions({
                organisationId,
            }: {
                organisationId: string;
            }) {
                return api.queryOptions(
                    'get',
                    '/api/v2/electrical/observationstandardtexts/items/list',
                    {
                        params: {
                            query: {
                                OrganizationId: organisationId,
                                PageNumber: 1,
                                PageSize: 100,
                                Select: {
                                    ObjectKind: true,
                                    Description: true,
                                    Order: true,
                                    TextKind: true,
                                },
                                Sort: ['Order'],
                            },
                        },
                    },
                );
            },
            checkListOptions({ organisationId }: { organisationId: string }) {
                return api.queryOptions('get', '/api/v2/electrical/checklists/lists/list', {
                    params: {
                        query: {
                            OrganizationId: organisationId,
                            PageNumber: 1,
                            PageSize: 100,
                            Status: {
                                Filters: [
                                    {
                                        Value: 'Active',
                                        ExpressionKind: 'Equal',
                                        IsIncludeNullValues: false,
                                    },
                                ],
                            },
                            Select: {
                                Status: true,
                                Description: true,
                            },
                            Sort: ['Status'],
                        },
                    },
                });
            },
            normsGetOptions({
                electricalInspectionNormListId,
            }: {
                electricalInspectionNormListId: string;
            }) {
                return api.queryOptions('get', '/api/v2/electrical/inspectionnorms/items/list', {
                    params: {
                        query: {
                            ElectricalInspectionNormListId: electricalInspectionNormListId,
                            Select: {
                                Description: true,
                                IsCustomInput: true,
                                NormKind: true,
                                Order: true,
                            },
                            Sort: ['Order'],
                            PageNumber: 1,
                            PageSize: 100,
                        },
                    },
                });
            },
            checkListItemsGetOptions({
                electricalChecklistListId,
            }: {
                electricalChecklistListId: string;
            }) {
                return api.queryOptions('get', '/api/v2/electrical/checklists/items/list', {
                    params: {
                        query: {
                            ElectricalCheckListId: electricalChecklistListId,
                            Select: {
                                Description: true,
                                Order: true,
                                Kind: true,
                            },
                            Sort: ['Order'],
                            PageNumber: 1,
                            PageSize: 100,
                        },
                    },
                });
            },
            checkListItemTextGetOptions({
                electricalChecklistItemId,
            }: {
                electricalChecklistItemId: string;
            }) {
                return api.queryOptions('get', '/api/v2/electrical/checklists/texts/list', {
                    params: {
                        query: {
                            ElectricalCheckListItemId: electricalChecklistItemId,
                            Select: {
                                Text: true,
                                Order: true,
                                Kind: true,
                            },
                            Sort: ['Order'],
                            PageNumber: 1,
                            PageSize: 100,
                        },
                    },
                });
            },
            documentTemplatesOptions({ organisationId }: { organisationId: string }) {
                return api.queryOptions('get', '/api/v2/documents/templates/list', {
                    params: {
                        query: {
                            TemplateKind: 'Electrical',
                            OrganizationId: organisationId,
                            PageNumber: 1,
                            PageSize: 100,
                            Select: {
                                Enabled: true,
                                IsDefault: true,
                                ContentKind: true,
                                TemplateKind: true,
                                GroupName: true,
                                IntervalKind: true,
                                Name: true,
                                Description: true,
                            },
                            Sort: ['Order'],
                        },
                    },
                });
            },
            measurementDevicesOptions({ organisationId }: { organisationId: string }) {
                return api.queryOptions('get', '/api/v2/electrical/measurementdevices/list', {
                    params: {
                        query: {
                            OrganizationId: organisationId,
                            PageNumber: 1,
                            PageSize: 100,
                            Select: {
                                BrandName: true,
                                Description: true,
                                ModelName: true,
                                Order: true,
                                CalibrationKind: true,
                                CalibrationInterval: true,
                                DeviceType: true,
                                IsCalibrated: true,
                                IsRetired: true,
                                SerialNumber: true,
                                Status: true,
                                Name: true,
                                UserDisplayName: true,
                                EndDate: true,
                                InspectionKinds: true,
                                StartDate: true,
                            },
                            Sort: ['Name'],
                        },
                    },
                });
            },

            // TODO @lukas - implement this

            // const measurementDevicesCalibrations = ({ measurementDeviceId }: { measurementDeviceId: string }) =>
            //     api.queryOptions('get', '/api/v2/electrical/measurementdevices/ca', {
            //         params: {
            //             query: {
            //                 ElectricalMeasurementDeviceId
            //                 PageNumber: 1,
            //                 PageSize: 100,
            //                 Select: {
            //
            //                 },
            //                 Sort: ['CreateDate'],
            //             },
            //         },
            //     });

            templatesCreateGetOptions({ organisationId }: { organisationId: string }) {
                return api.queryOptions('get', '/api/v2/documents/templates/create', {
                    params: {
                        query: {
                            OrganizationId: organisationId,
                            TemplateKind: 'Electrical',
                        },
                    },
                });
            },

        },
    },
};
